/**
 * VS Code's quick input: a box at the top of the editor area with a filter
 * field and a list under it. Quick open, the command palette, go to line and
 * go to symbol are all this box with different providers; the prefix typed in
 * the field (">", ":", "@", "#") switches between them, as it does in VS Code.
 */

export interface QuickItem {
    label: string;
    description?: string;
    icon?: HTMLElement;
    keybinding?: string;
    /** Character positions of `label` to highlight. */
    highlights?: number[];
    accept: () => void;
    /** Shown before the query is typed; filtered and scored by the provider. */
    separator?: string;
}

export interface QuickProvider {
    prefix: string;
    placeholder: string;
    /** Items for the query, which has the prefix removed. */
    items(query: string): QuickItem[] | Promise<QuickItem[]>;
    /** Accept on Enter even with no item, e.g. go to line. */
    acceptEmpty?: (query: string) => boolean;
    /** What to type, shown in the list while the query is empty and nothing matches. */
    hint?: string;
}

const MAX_ITEMS = 200;

export class QuickInput {
    readonly element: HTMLElement;
    readonly #input: HTMLInputElement;
    readonly #list: HTMLElement;
    readonly #providers: QuickProvider[] = [];
    #items: QuickItem[] = [];
    #selected = 0;
    #generation = 0;
    #restoreFocus: HTMLElement | null = null;
    #only: QuickProvider | undefined;
    readonly #shown = new Set<(prefix: string) => void>();
    #onHide: (() => void) | undefined;

    constructor() {
        this.element = document.createElement("div");
        this.element.className = "wb-quick-input";
        this.element.hidden = true;
        this.#input = document.createElement("input");
        this.#input.type = "text";
        this.#input.spellcheck = false;
        this.#input.setAttribute("aria-autocomplete", "list");
        this.#list = document.createElement("div");
        this.#list.className = "wb-quick-list";
        this.#list.setAttribute("role", "listbox");
        this.element.append(this.#input, this.#list);
        this.#input.addEventListener("input", () => void this.#update());
        this.#input.addEventListener("keydown", (event) => this.#key(event));
        this.#input.addEventListener("blur", () => {
            // A click on an item blurs the field first; let the click land.
            setTimeout(() => {
                if (!this.element.contains(document.activeElement)) this.hide();
            }, 150);
        });
    }

    register(provider: QuickProvider): void {
        this.#providers.push(provider);
        // Longest prefix first, so "" is the fallback.
        this.#providers.sort((a, b) => b.prefix.length - a.prefix.length);
    }

    /** The prefix of the provider shown, or undefined while the box is hidden. */
    get prefix(): string | undefined {
        return this.element.hidden ? undefined : this.#provider().provider.prefix;
    }

    /** Open with `text` in the field; `only` pins one provider regardless of prefix. */
    show(text: string, only?: QuickProvider): void {
        if (this.element.hidden) {
            this.#restoreFocus = document.activeElement as HTMLElement | null;
        }
        this.#only = only;
        this.element.hidden = false;
        this.#input.value = text;
        this.#input.focus();
        this.#input.setSelectionRange(text.length, text.length);
        void this.#update();
        if (only === undefined) for (const listener of this.#shown) listener(this.#provider().provider.prefix);
    }

    /** Call `listener` with the prefix each time the box opens on its providers, not on a question. */
    onShow(listener: (prefix: string) => void): void {
        this.#shown.add(listener);
    }

    /** Ask for a line of text, as VS Code's input box does; undefined on Escape. */
    ask(prompt: string, value = "", select?: [number, number]): Promise<string | undefined> {
        return new Promise((resolve) => {
            let answer: string | undefined;
            this.show(value, {
                prefix: "",
                placeholder: prompt,
                items: () => [],
                acceptEmpty: (query) => {
                    answer = query;
                    return query !== "";
                },
            });
            if (select !== undefined) this.#input.setSelectionRange(...select);
            this.#onHide = () => resolve(answer);
        });
    }

    hide(restore = true): void {
        if (this.element.hidden) return;
        this.element.hidden = true;
        this.#only = undefined;
        const onHide = this.#onHide;
        this.#onHide = undefined;
        onHide?.();
        if (restore) this.#restoreFocus?.focus();
    }

    #provider(): { provider: QuickProvider; query: string } {
        const text = this.#input.value;
        if (this.#only !== undefined) return { provider: this.#only, query: text };
        const provider = this.#providers.find((p) => text.startsWith(p.prefix))!;
        return { provider, query: text.slice(provider.prefix.length) };
    }

    async #update(): Promise<void> {
        const generation = ++this.#generation;
        const { provider, query } = this.#provider();
        this.#input.placeholder = provider.placeholder;
        const items = await provider.items(query.trim());
        if (generation !== this.#generation) return;
        this.#items = items.slice(0, MAX_ITEMS);
        this.#selected = 0;
        this.#draw();
    }

    #draw(): void {
        const rows = this.#items.map((item, index) => {
            const row = document.createElement("div");
            row.className = "wb-quick-item";
            row.setAttribute("role", "option");
            if (index === this.#selected) row.classList.add("selected");
            if (item.separator !== undefined) {
                const separator = document.createElement("span");
                separator.className = "wb-quick-separator";
                separator.textContent = item.separator;
                row.append(separator);
            }
            if (item.icon !== undefined) row.append(item.icon);
            const label = document.createElement("span");
            label.className = "wb-quick-label";
            label.append(...highlighted(item.label, item.highlights ?? []));
            row.append(label);
            if (item.description !== undefined) {
                const description = document.createElement("span");
                description.className = "wb-quick-description";
                description.textContent = item.description;
                row.append(description);
            }
            if (item.keybinding !== undefined) {
                const key = document.createElement("kbd");
                key.textContent = item.keybinding;
                row.append(key);
            }
            row.addEventListener("mousedown", (event) => event.preventDefault());
            row.addEventListener("click", () => this.#accept(index));
            return row;
        });
        if (rows.length === 0) {
            const none = document.createElement("div");
            none.className = "wb-quick-empty";
            const { provider, query } = this.#provider();
            none.textContent =
                provider.acceptEmpty !== undefined
                    ? `${provider.placeholder} (Enter to confirm, Escape to cancel)`
                    : provider.hint !== undefined && query.trim() === ""
                      ? provider.hint
                      : "No matching results";
            rows.push(none);
        }
        this.#list.replaceChildren(...rows);
        this.#list.children[this.#selected]?.scrollIntoView({ block: "nearest" });
    }

    #accept(index: number): void {
        const item = this.#items[index];
        if (item === undefined) {
            const { provider, query } = this.#provider();
            if (provider.acceptEmpty?.(query.trim())) this.hide(true);
            return;
        }
        this.hide(false);
        item.accept();
    }

    #key(event: KeyboardEvent): void {
        switch (event.key) {
            case "Escape":
                this.hide();
                break;
            case "ArrowDown":
                this.#selected = Math.min(this.#items.length - 1, this.#selected + 1);
                this.#draw();
                break;
            case "ArrowUp":
                this.#selected = Math.max(0, this.#selected - 1);
                this.#draw();
                break;
            case "PageDown":
                this.#selected = Math.min(this.#items.length - 1, this.#selected + 10);
                this.#draw();
                break;
            case "PageUp":
                this.#selected = Math.max(0, this.#selected - 10);
                this.#draw();
                break;
            case "Enter":
                this.#accept(this.#selected);
                break;
            default:
                return;
        }
        event.preventDefault();
        event.stopPropagation();
    }
}

function highlighted(text: string, positions: number[]): Node[] {
    if (positions.length === 0) return [document.createTextNode(text)];
    const set = new Set(positions);
    const nodes: Node[] = [];
    let run = "";
    let marked = false;
    const flush = () => {
        if (run === "") return;
        if (marked) {
            const mark = document.createElement("mark");
            mark.textContent = run;
            nodes.push(mark);
        } else nodes.push(document.createTextNode(run));
        run = "";
    };
    for (let i = 0; i < text.length; i++) {
        const m = set.has(i);
        if (m !== marked) {
            flush();
            marked = m;
        }
        run += text[i];
    }
    flush();
    return nodes;
}

/**
 * Fuzzy match as quick open does it: the query's characters in order, scored
 * higher for consecutive runs and for matches at word starts. Returns the
 * matched positions, or undefined when the query does not match.
 */
export function fuzzy(query: string, text: string): { score: number; positions: number[] } | undefined {
    if (query === "") return { score: 0, positions: [] };
    const q = query.toLowerCase();
    const t = text.toLowerCase();
    const positions: number[] = [];
    let score = 0;
    let from = 0;
    let previous = -2;
    for (const ch of q) {
        if (ch === " ") continue;
        const at = t.indexOf(ch, from);
        if (at < 0) return undefined;
        const start = at === 0 || /[\/_\-.\s]/.test(t[at - 1]!) || (text[at] !== t[at] && text[at - 1] === t[at - 1]);
        score += 1 + (at === previous + 1 ? 5 : 0) + (start ? 3 : 0);
        positions.push(at);
        previous = at;
        from = at + 1;
    }
    return { score: score - text.length * 0.01, positions };
}
